import { useRef, useEffect, useState, useCallback } from "react";
import { Alert, Tag } from "antd";
import { LogsPageProps } from "../types";
import { useLogs } from "../hooks/useLogs";
import { useLogsUrlState } from "../hooks/useLogsUrlState";
import { LogsToolbar } from "./LogsToolbar";
import { LogsViewer } from "./LogsViewer";
import { getQueryLabel, setQueryLabel, removeQueryLabel, isQueryParseable } from "../utils/queryParser";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { selectValue as selectTimezone, selectTimezoneMode, setValue as setTimezoneValue } from "../../../store/timezoneSlice";
import { getLocalToken } from "../../../services/cpln";
import { METRICS_TOKEN_UPDATE_INTERVAL_MS } from "../../../envVariables";
import "./LogsPage.css";

/**
 * Container component for logs functionality
 * Composes toolbar and viewer with shared state from useLogs hook
 * Uses window scroll for the logs viewer - the entire page scrolls
 */
export function LogsPage({
  org,
  defaultQuery,
  storageKey,
  fileNamePrefix,
  logsEndpoint,
  grafanaEndpoint,
  beforeQuery,
  filterSlot,
  disabled = false,
  disabledMessage,
  filters,
}: LogsPageProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const grafanaFormRef = useRef<HTMLFormElement>(null);
  const [viewerWidth, setViewerWidth] = useState(0);
  const [isQueryFocused, setIsQueryFocused] = useState(false);
  const [logsToken, setLogsToken] = useState("");

  const dispatch = useAppDispatch();

  // Fetch and refresh auth token for Grafana
  useEffect(() => {
    getLocalToken().then((res) => {
      if (res.accessToken !== "pass") {
        setLogsToken(res.accessToken);
      }
    });
    const localTokenInterval = setInterval(() => {
      getLocalToken().then((res) => {
        if (res.accessToken !== "pass") {
          setLogsToken(res.accessToken);
        }
      });
    }, METRICS_TOKEN_UPDATE_INTERVAL_MS);
    return () => {
      clearInterval(localTokenInterval);
    };
  }, []);

  // Get timezone from Redux
  const timezone = useAppSelector(selectTimezone);
  const timezoneMode = useAppSelector(selectTimezoneMode);

  // Callback to update global timezone from URL (e.g., when linking from job executions with UTC times)
  const handleTimezoneChange = useCallback((tz: string) => {
    dispatch(setTimezoneValue(tz));
  }, [dispatch]);

  // Use the logs hook
  const logsState = useLogs({
    org,
    defaultQuery,
    storageKey,
    fileNamePrefix,
    logsEndpoint,
    grafanaEndpoint,
    beforeQuery,
    timezone,
  });

  const {
    logs,
    commonLabels,
    hasUniqueLabels,
    query,
    setQuery,
    from,
    to,
    setFrom,
    setTo,
    isLoading,
    isLive,
    isLiveDirty,
    liveStatus,
    hasExecuted,
    executeQuery,
    cancelQuery,
    startLive,
    stopLive,
    restartLive,
    downloadLogs,
    copyLogs,
    resetQuery,
    settings,
    updateSettings,
    grafanaRedirectUrl,
    error,
    bufferedCount,
    setLivePaused,
  } = logsState;

  // Live pause handlers
  const handleLivePauseChange = useCallback(
    (paused: boolean) => {
      setLivePaused(paused);
    },
    [setLivePaused]
  );

  const handleLiveResume = useCallback(() => {
    setLivePaused(false);
  }, [setLivePaused]);

  // Handle opening Grafana by submitting the hidden form
  const handleOpenGrafana = useCallback(() => {
    if (grafanaFormRef.current) {
      grafanaFormRef.current.submit();
    }
  }, []);

  // Sync URL with logs state (use timezoneMode for URL - "UTC" or "local", not full tz name)
  const { shouldExecute, shouldStartLive, markUrlTriggersHandled, isUrlInitialized } = useLogsUrlState({
    logs: logsState,
    isInputFocused: isQueryFocused,
    timezone: timezoneMode,
    onTimezoneChange: handleTimezoneChange,
  });

  // Handle URL-triggered execution
  useEffect(() => {
    if (shouldStartLive) {
      startLive();
      markUrlTriggersHandled();
    } else if (shouldExecute) {
      executeQuery();
      markUrlTriggersHandled();
    }
  }, [shouldExecute, shouldStartLive, startLive, executeQuery, markUrlTriggersHandled]);

  // Track if Query → Filter sync has run at least once after URL init
  const filterSyncedRef = useRef(false);

  // Helper to insert a label into an unparseable query
  const insertLabelIntoUnparseableQuery = (q: string, key: string, value: string): string => {
    const labelStr = `${key}="${value}"`;
    const braceStart = q.indexOf("{");
    const braceEnd = q.indexOf("}");

    if (braceStart !== -1) {
      // Insert right after {
      const afterBrace = q.slice(braceStart + 1).trim();
      const separator = afterBrace && !afterBrace.startsWith("}") ? ", " : "";
      return q.slice(0, braceStart + 1) + labelStr + separator + q.slice(braceStart + 1);
    } else if (braceEnd !== -1) {
      // Insert right before }
      const beforeBrace = q.slice(0, braceEnd).trim();
      const separator = beforeBrace ? ", " : "";
      return q.slice(0, braceEnd) + separator + labelStr + q.slice(braceEnd);
    } else {
      // Prepend with opening brace only - we don't know how to end the query
      return `{${labelStr}, ` + q;
    }
  };

  // Filter → Query sync: when filter dropdown changes, update the query
  // Wait until URL is initialized AND Query → Filter sync has run
  useEffect(() => {
    if (!filters || filters.length === 0) return;
    if (!isUrlInitialized) return; // Wait for URL to be read first
    if (!filterSyncedRef.current) return; // Wait for Query → Filter sync to run first

    const queryIsParseable = isQueryParseable(query);
    let newQuery = query;

    for (const filter of filters) {
      const anyVal = filter.anyValue ?? "any";
      const customVal = filter.customValue ?? "custom";

      // Don't modify query when "custom" is selected - it's just an indicator
      if (filter.value === customVal) continue;

      // Skip "any" for unparseable queries - we can't safely remove labels
      if (!queryIsParseable && filter.value === anyVal) continue;

      if (filter.value === anyVal) {
        // Remove the key from query when "any" is selected
        newQuery = removeQueryLabel(newQuery, filter.key);
      } else {
        if (queryIsParseable) {
          // Normal case: set or update the key in query
          const currentVal = getQueryLabel(newQuery, filter.key);
          if (currentVal !== filter.value) {
            newQuery = setQueryLabel(newQuery, filter.key, filter.value);
          }
        } else {
          // Unparseable query: insert the label at appropriate position
          newQuery = insertLabelIntoUnparseableQuery(newQuery, filter.key, filter.value);
        }
      }
    }

    if (newQuery !== query) {
      setQuery(newQuery);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters?.map((f) => `${f.key}:${f.value}`).join(","), isUrlInitialized]);

  // Build a string key for filter options to detect when they change
  const filterOptionsKey = filters
    ?.map((f) => `${f.key}:${f.options?.map((o) => o.value).join("|") ?? ""}`)
    .join(",");

  // Query → Filter sync: when query changes or filter options load, update filter dropdowns
  // Only sync when input is not focused (wait for blur)
  useEffect(() => {
    if (!filters || filters.length === 0) return;
    if (!isUrlInitialized) return; // Wait for URL to be read first
    if (isQueryFocused) return; // Don't sync while typing
    if (!isQueryParseable(query)) return; // Don't sync invalid queries - leave dropdown as-is

    for (const filter of filters) {
      const queryVal = getQueryLabel(query, filter.key);
      const anyVal = filter.anyValue ?? "any";
      const customVal = filter.customValue ?? "custom";

      if (!queryVal) {
        // No value in query for this key - set to "any" if not already
        if (filter.value !== anyVal) {
          filter.onChange(anyVal);
        }
      } else if (queryVal !== filter.value) {
        // Value exists in query but doesn't match dropdown
        // Check if it's a known option
        const isKnownOption = filter.options?.some((opt) => opt.value === queryVal);
        if (isKnownOption) {
          filter.onChange(queryVal);
        } else if (filter.value !== customVal) {
          // Unknown value and not already showing "custom" - set to custom
          filter.onChange(customVal);
        }
      }
    }

    // Mark that Query → Filter sync has run, allowing Filter → Query sync
    filterSyncedRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, isQueryFocused, filterOptionsKey, isUrlInitialized]);

  // Click label to add to filter query
  const queryRef = useRef(query);
  queryRef.current = query;

  const handleLabelClick = useCallback(
    (key: string, value: string) => {
      setQuery(setQueryLabel(queryRef.current, key, value));
    },
    [setQuery]
  );

  // Callback for query input focus state
  const handleQueryFocus = useCallback(() => setIsQueryFocused(true), []);
  const handleQueryBlur = useCallback(() => setIsQueryFocused(false), []);

  // Track container width for the viewer
  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        const containerRect = containerRef.current.getBoundingClientRect();
        setViewerWidth(containerRect.width - 32); // 16px padding on each side
      }
    };

    // Initial calculation with a small delay to ensure layout is complete
    const timeoutId = setTimeout(updateWidth, 50);

    // Create ResizeObserver for responsive updates
    const resizeObserver = new ResizeObserver(updateWidth);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    window.addEventListener("resize", updateWidth);

    return () => {
      clearTimeout(timeoutId);
      resizeObserver.disconnect();
      window.removeEventListener("resize", updateWidth);
    };
  }, []);

  // Show disabled message
  if (disabled && disabledMessage) {
    return (
      <div className="logs-page" ref={containerRef}>
        <Alert
          type="warning"
          message={disabledMessage}
          showIcon
          style={{ margin: 16 }}
        />
      </div>
    );
  }

  return (
    <div className="logs-page" ref={containerRef}>
      {/* Hidden form for Grafana authentication */}
      <form
        className="hidden"
        ref={grafanaFormRef}
        action={`${grafanaEndpoint}/auth/process`}
        method="POST"
        target="_blank"
      >
        <input name="redirect" value={grafanaRedirectUrl} type="hidden" />
        <input name="idToken" value={logsToken} type="hidden" />
        <input name="genericToken" value={logsToken} type="hidden" />
      </form>

      <LogsToolbar
        query={query}
        onQueryChange={setQuery}
        from={from}
        to={to}
        onFromChange={setFrom}
        onToChange={setTo}
        onExecute={executeQuery}
        onCancel={cancelQuery}
        onLive={startLive}
        onStopLive={stopLive}
        onRestartLive={restartLive}
        isLoading={isLoading}
        isLive={isLive}
        isLiveDirty={isLiveDirty}
        liveStatus={liveStatus}
        onDownload={downloadLogs}
        onCopy={copyLogs}
        onOpenGrafana={handleOpenGrafana}
        onReset={resetQuery}
        disabled={disabled}
        filterSlot={filterSlot}
        settings={settings}
        onSettingsChange={updateSettings}
        hasUniqueLabels={hasUniqueLabels}
        onQueryFocus={handleQueryFocus}
        onQueryBlur={handleQueryBlur}
        logsEndpoint={logsEndpoint}
        org={org}
      />

      {/* Common labels display */}
      {commonLabels.length > 0 && logs.length > 0 && (
        <div className="logs-page-common-labels">
          <span className="logs-page-common-labels-title">Common labels:</span>
          {commonLabels.map((label) => {
            const eqIndex = label.indexOf("=");
            const key = eqIndex > 0 ? label.slice(0, eqIndex) : label;
            const value = eqIndex > 0 ? label.slice(eqIndex + 1) : "";
            return (
              <Tag
                key={label}
                className="logs-page-common-label"
                style={{ cursor: "pointer" }}
                onClick={() => handleLabelClick(key, value)}
              >
                {label}
              </Tag>
            );
          })}
        </div>
      )}

      <div className="logs-page-viewer">
        <LogsViewer
          logs={logs}
          isLoading={isLoading}
          isLive={isLive}
          liveStatus={liveStatus}
          settings={settings}
          commonLabels={commonLabels}
          width={viewerWidth}
          error={error}
          hasExecuted={hasExecuted}
          hasUniqueLabels={hasUniqueLabels}
          bufferedCount={bufferedCount}
          onPauseChange={handleLivePauseChange}
          onResume={handleLiveResume}
          onLabelClick={handleLabelClick}
        />
      </div>
    </div>
  );
}
